import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FaHome, FaTruck, FaFlask, FaChartBar, FaUserCircle, FaSignOutAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/AuthSlice";

const Header = () => {
    const authUser = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setProfileOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ------------------ ROLE MENU LOGIC ---------------------

    const getMenusForRole = () => {
        if (!authUser?.roleId) return [];

        switch (authUser.roleId) {
            case 1:
                return ["gate", "milk", "report"]; // ALL
            case 2:
                return ["report"];
            case 3:
                return ["gate"];
            case 4:
                return ["milk"];
            default:
                return [];
        }
    };

    const getDashboardRoute = () => {
        if (authUser?.roleId === 2) {
            return "/app/admin-dashboard";   // role 2 special dashboard
        }
        return "/app"; // all other roles use same dashboard
    };


    const allowedMenus = getMenusForRole();
    const showMenu = (key) => allowedMenus.includes(key);

    // Logout
    const handleLogout = () => {
        sessionStorage.removeItem("auth");
        dispatch(logout());
        navigate("/");
    };

    // Common class for menu items
    const linkClass = ({ isActive }) =>
        isActive
            ? "text-indigo-600 font-semibold border-b-2 border-indigo-100 pb-1 flex items-center space-x-2"
            : "text-gray-700 hover:text-indigo-600 transition flex items-center space-x-2";

    // ---------------------------------------------------------

    return (
        <header className="bg-white shadow-sm sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">

                    {/* Brand */}
                    <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                        flex items-center justify-center text-white font-extrabold text-xl">
                            ML
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-gray-900 leading-tight">Milk Dairy</div>
                        </div>
                    </div>

                    {/* Desktop Menu */}
                    <nav className="hidden md:flex items-center space-x-8">

                        {/* {showMenu("dashboard") && (
                            <NavLink to={getDashboardRoute()} end className={linkClass}>
                                <FaHome className="text-lg text-purple-600" />
                                <span className="font-semibold">Dashboard</span>
                            </NavLink>

                        )} */}

                        {showMenu("gate") && (
                            <NavLink to="/app/gate-entry" end className={linkClass}>
                                <FaTruck className="text-lg text-purple-600" />
                                <span className="font-semibold">Gate Entry</span>
                            </NavLink>
                        )}

                        {showMenu("milk") && (
                            <NavLink to="/app/lab-report" end className={linkClass}>
                                <FaFlask className="text-lg text-purple-600" />
                                <span className="font-semibold">Lab Report</span>
                            </NavLink>
                        )}

                        {showMenu("report") && (
                            <NavLink to="/app/test-report" end className={linkClass}>
                                <FaChartBar className="text-lg text-purple-600" />
                                <span className="font-semibold">Report</span>
                            </NavLink>
                        )}

                        {/* Profile */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                aria-label="Profile"
                                onClick={() => setProfileOpen(!profileOpen)}
                                className="ml-4 text-gray-700 hover:text-indigo-600 transition flex items-center space-x-2"
                            >
                                <FaUserCircle className="text-3xl text-purple-600" />
                                <span className="font-medium text-purple-800">{authUser?.name}</span>
                            </button>

                            {profileOpen && (
                                <div className="absolute left-0 mt-3 w-40 bg-white shadow-lg rounded-md py-2 border">
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                                    >
                                        <FaSignOutAlt className="text-lg text-purple-600" />
                                        <span className="font-semibold">Logout</span>
                                    </button>
                                </div>
                            )}
                        </div>

                    </nav>

                    {/* Mobile Toggler */}
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setOpen(!open)}
                            className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
                        >
                            {open ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor">
                                    <path strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor">
                                    <path strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                    </div>

                </div>
            </div>

            {/* Mobile Menu */}
            {/* Mobile Menu */}
            {open && (
                <div className="md:hidden bg-white shadow-lg rounded-lg mt-3 p-4 space-y-4">

                    {/* Gate Entry */}
                    {showMenu("gate") && (
                        <NavLink
                            to="/app/gate-entry"
                            end
                            onClick={() => setOpen(false)}
                            className="flex items-center space-x-3 text-gray-700 hover:text-indigo-600"
                        >
                            <FaTruck className="text-lg text-purple-600" />
                            <span className="font-semibold">Gate Entry</span>
                        </NavLink>
                    )}

                    {/* Lab Report */}
                    {showMenu("milk") && (
                        <NavLink
                            to="/app/lab-report"
                            end
                            onClick={() => setOpen(false)}
                            className="flex items-center space-x-3 text-gray-700 hover:text-indigo-600"
                        >
                            <FaFlask className="text-lg text-purple-600" />
                            <span className="font-semibold">Lab Report</span>
                        </NavLink>
                    )}

                    {/* Report */}
                    {showMenu("report") && (
                        <NavLink
                            to="/app/test-report"
                            end
                            onClick={() => setOpen(false)}
                            className="flex items-center space-x-3 text-gray-700 hover:text-indigo-600"
                        >
                            <FaChartBar className="text-lg text-purple-600" />
                            <span className="font-semibold">Report</span>
                        </NavLink>
                    )}

                    {/* ⭐ MOBILE PROFILE SECTION */}
                    <div className="border-t pt-3">
                        <div className="flex items-center space-x-2">
                            <FaUserCircle className="text-lg text-purple-600" />
                            <span className="font-medium text-purple-800">{authUser?.name}</span>
                        </div>

                        <button
                            onClick={() => {
                                setOpen(false);
                                handleLogout();
                            }}
                            className="flex items-center gap-2 w-full text-left py-2 text-gray-700 hover:bg-gray-100"
                        >
                            <FaSignOutAlt className="text-lg text-purple-600" />
                            <span className="font-semibold">Logout</span>
                        </button>
                    </div>

                </div>
            )}


        </header>
    );
};

export default Header;
